import React from 'react';
import { BrowserRouter, Routes, Route, useParams, useSearchParams } from 'react-router-dom';
import YouTubePlayer from './components/YouTubePlayer';
import './App.css';

/**
 * Página del reproductor: toma el UUID de la URL y los parámetros de query.
 * Misma lógica de query params que usa videoRouter en el backend:
 *   - autoplay (default: true, se desactiva con ?autoplay=0)
 *   - loop (default: false, se activa con ?loop=1)
 *   - mute (default: true, se desactiva con ?mute=0)
 *   - captions (default: false, se activa con ?captions=1)
 */
const PlayerPage: React.FC = () => {
  const { uuid } = useParams<{ uuid: string }>();
  const [searchParams] = useSearchParams();

  const autoplay = searchParams.get('autoplay') !== '0';
  const loop = searchParams.get('loop') === '1';
  const mute = searchParams.get('mute') !== '0';
  const captions = searchParams.get('captions') === '1';

  if (!uuid) {
    return (
      <div className="player-status error">
        <h1>Error</h1>
        <p>No se proporcionó un ID de video</p>
      </div>
    );
  }

  return (
    <YouTubePlayer
      videoIdentifier={uuid}
      resolveFromAPI={true}
      autoplay={autoplay}
      loop={loop}
      mute={mute}
      captions={captions}
    />
  );
};

/**
 * Página de reproducción directa: usa un YouTube video ID directamente.
 * Útil para testing sin depender de la API.
 * Ruta: /direct/:videoId
 */
const DirectPlayerPage: React.FC = () => {
  const { videoId } = useParams<{ videoId: string }>();
  const [searchParams] = useSearchParams();

  const autoplay = searchParams.get('autoplay') !== '0';
  const loop = searchParams.get('loop') === '1';
  const mute = searchParams.get('mute') !== '0';
  const captions = searchParams.get('captions') === '1';

  if (!videoId) {
    return (
      <div className="player-status error">
        <h1>Error</h1>
        <p>No se proporcionó un video ID de YouTube</p>
      </div>
    );
  }

  return (
    <YouTubePlayer
      videoIdentifier={videoId}
      resolveFromAPI={false}
      autoplay={autoplay}
      loop={loop}
      mute={mute}
      captions={captions}
    />
  );
};

const App: React.FC = () => {
  return (
    <BrowserRouter>
      <Routes>
        {/* Reproduce por UUID (consulta a la API) */}
        <Route path="/video/:uuid" element={<PlayerPage />} />

        {/* Reproduce directamente por YouTube video ID */}
        <Route path="/direct/:videoId" element={<DirectPlayerPage />} />

        {/* Página de inicio */}
        <Route
          path="/"
          element={
            <div className="player-status">
              <h1>YouTube Player</h1>
              <p>Accede a un video usando:</p>
              <code>/video/:uuid</code>
              <p style={{ marginTop: '10px', opacity: 0.7 }}>
                o directamente con un YouTube ID:
              </p>
              <code>/direct/:youtubeVideoId</code>
            </div>
          }
        />

        {/* 404 */}
        <Route
          path="*"
          element={
            <div className="player-status error">
              <h1>404</h1>
              <p>Página no encontrada</p>
            </div>
          }
        />
      </Routes>
    </BrowserRouter>
  );
};

export default App;
